import type { OriginalsConfig } from '../types.js';
import { originalTheme, tierColors } from '../theme.js';

interface RingSlice {
  multiplierBp: number;
  label: string;
  color: string;
}

/**
 * Distinct-multiplier legend shown under the wheel. The wheel itself draws
 * colour-only wedges at high segment counts (per-wedge text is unreadable when
 * dense), so this strip is the readable key — each chip pairs the wedge colour
 * with its multiplier, like the multiplier row under the bc.game wheel.
 */
export function WheelLegend({ config }: { config: OriginalsConfig }) {
  const raw = (config.presenterConfig as { ring?: RingSlice[] } | null | undefined)?.ring ?? [];
  if (!Array.isArray(raw) || raw.length === 0) return null;
  // Same tier colours as the wheel draws; a ring without multipliers keeps the server's.
  const ring = raw.every((s) => s && typeof s === 'object' && typeof s.multiplierBp === 'number')
    ? tierColors(raw, originalTheme('wheel')?.tiers)
    : raw;

  const distinct = new Map<number, RingSlice>();
  for (const slice of ring) {
    if (slice && typeof slice === 'object' && !distinct.has(slice.multiplierBp)) {
      distinct.set(slice.multiplierBp, slice);
    }
  }
  const items = [...distinct.values()].sort((a, b) => a.multiplierBp - b.multiplierBp);
  if (items.length === 0) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
      {items.map((s) => (
        <span
          key={s.multiplierBp}
          className="inline-flex items-center gap-2 h-9 px-3.5 rounded-xl bg-[var(--bg-elevated)] text-[13px] font-bold tabular-nums text-[var(--text-primary)]"
        >
          <span
            className="w-3 h-3 rounded-full shrink-0 border border-[var(--text-muted)]"
            style={{ backgroundColor: s.color }}
            aria-hidden
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}
